import { MutableRefObject, useEffect, useReducer } from "react";
import { formatDataSource, getMoveDataSource } from "../util";
import { EdiTableDispatchType, GROUP_NAME, IEdiTablePayload } from "../interface";
import { HISTORY_LIST_DATA, STATUS_CREATED, STATUS_CREATED_CODE } from "../../constants/constant";

export interface IDetailPageAction {
  type: EdiTableDispatchType;
  payload: IEdiTablePayload;
}

export interface IBaseEdiTableContext {
  dataSource?: any[];
}

// Action
function EdiTableContextAction(
  state: IBaseEdiTableContext,
  action: IDetailPageAction,
): IBaseEdiTableContext {
  const oldDataSource = state.dataSource || [];
  const { dataSource, updateDataSource, addItem, appendItem, flagIndex, isEmpty, deleteKeys, startIndex, endIndex, form, rowData, } = action.payload;
  switch (action.type) {
    case 'init':
      if (
        (!oldDataSource || !oldDataSource.length) &&
        (!dataSource || !dataSource.length)
      ) {
        return {
          ...state,
        };
      }
      return {
        ...state,
        dataSource: dataSource?.map((item, index) => ({
          ...item,
          key: index,
          [HISTORY_LIST_DATA]: true,
          [STATUS_CREATED_CODE]:
            item[STATUS_CREATED_CODE] || STATUS_CREATED.DEFAULT,
        })),
      };
    case 'changeDataSource':
      return {
        ...state,
        dataSource: updateDataSource,
      };
    case 'addRow':
      let key = flagIndex || 0;
      let addDataSource: any[] = [...oldDataSource];
      if (isEmpty) {
        addDataSource = [];
        oldDataSource.forEach(item => {
          if (item[STATUS_CREATED_CODE] !== STATUS_CREATED.ADD) {
            addDataSource.push({
              ...item,
              [STATUS_CREATED_CODE]: STATUS_CREATED.DELETE,
            });
          }
        });
      }
      if (Array.isArray(addItem)) {
        addItem.forEach((item, index) => {
          addDataSource.push({
            [STATUS_CREATED_CODE]: STATUS_CREATED.ADD,
            ...item,
            key: key + index,
          });
        });
      } else {
        addDataSource.push({
          [STATUS_CREATED_CODE]: STATUS_CREATED.ADD,
          ...addItem,
          key: key,
        });
      }
      return {
        ...state,
        dataSource: addDataSource,
      };
    case 'cover':
      let coverKey = flagIndex || 0;
      let coverDataSource: any[] = [];
      oldDataSource.forEach(item => {
        if (item[STATUS_CREATED_CODE] !== STATUS_CREATED.ADD) {
          coverDataSource.push({
            ...item,
            [STATUS_CREATED_CODE]: STATUS_CREATED.DELETE,
          });
        }
      });
      if (Array.isArray(addItem) && addItem.length) {
        addItem.forEach((item, index) => {
          coverDataSource.push({
            ...item,
            [STATUS_CREATED_CODE]: STATUS_CREATED.ADD,
            key: coverKey + index,
          });
        });
      }
      return {
        ...state,
        dataSource: coverDataSource,
      };
    case 'append':
      if (Array.isArray(appendItem) && appendItem.length) {
        let appendKey = flagIndex || 0;
        let appendOldDataSource: any[] = [...oldDataSource];
        let appendNewDataSource: any[] = [];
        appendItem.forEach(item => {
          const findIndex = oldDataSource.findIndex(v => v.key === item.key);
          if (findIndex >= 0) {
            const oldStatus = appendOldDataSource[findIndex][STATUS_CREATED_CODE];
            const newAppendItem = {
              ...item,
              [STATUS_CREATED_CODE]: oldStatus === STATUS_CREATED.DEFAULT ? STATUS_CREATED.UPDATE : oldStatus,
            };
            appendOldDataSource[findIndex] = newAppendItem;
            form?.setFieldValue([GROUP_NAME, item.key], newAppendItem);
          } else {
            appendNewDataSource.push({
              ...item,
              key: appendKey,
              [STATUS_CREATED_CODE]: STATUS_CREATED.ADD,
            });
            appendKey = appendKey + 1;
          }
        });
        return {
          ...state,
          dataSource: [...appendOldDataSource, ...appendNewDataSource],
        };
      }
      return state;
    case 'deleteRow':
      if (Array.isArray(deleteKeys) && deleteKeys.length) {
        const deleteDataSource: any[] = [];
        const otherDataSource: any[] = [];
        oldDataSource.forEach(item => {
          if (deleteKeys.includes(item.key)) {
            if (item[STATUS_CREATED_CODE] !== STATUS_CREATED.ADD) {
              deleteDataSource.push({
                ...item,
                [STATUS_CREATED_CODE]: STATUS_CREATED.DELETE,
              });
            }
          } else {
            otherDataSource.push(item);
          }
        });
        return {
          ...state,
          dataSource: [...deleteDataSource, ...otherDataSource],
        };
      }
      return state;
    case 'moveData':
      if (startIndex === undefined || endIndex === undefined || startIndex === endIndex) return state;
      const { effectiveData, deleteData, } = formatDataSource(oldDataSource);
      const moveDataSource = getMoveDataSource(effectiveData, startIndex, endIndex);
      return {
        ...state,
        dataSource: [...deleteData, ...moveDataSource],
      };
    case 'changeRowData':
      if (!rowData) return state;
      return {
        ...state,
        dataSource: oldDataSource.map(item => {
          if (item.key === rowData.key) {
            const oldStatus = item[STATUS_CREATED_CODE];
            return {
              ...item,
              ...rowData,
              [STATUS_CREATED_CODE]: oldStatus === STATUS_CREATED.DEFAULT ? STATUS_CREATED.UPDATE : oldStatus,
            };
          }
          return item;
        }),
      };
    default:
      return state;
  }
}

// 初始数据
const initEdiTableContext: IBaseEdiTableContext = {};

/**
 * useEdiTableContext
 * @param flagIndex 可编辑表末尾行指针
 * @param dataSource 可编辑表数据
 */
function useEdiTableContext(
  flagIndex: MutableRefObject<number>,
  dataSource?: any[],
) {
  const [EdiTableContext, dispatch] = useReducer(
    EdiTableContextAction,
    initEdiTableContext,
  );
  useEffect(() => {
    flagIndex.current = dataSource?.length || 0;
    dispatch({ type: 'init', payload: { dataSource } });
  }, [dataSource]);
  function dispatchEdiTable(
    type: EdiTableDispatchType,
    payload: IEdiTablePayload = {},
  ) {
    const { updateDataSource, addItem, appendItem, isEmpty, deleteKeys, startIndex, endIndex, form, rowData, } = payload;
    switch (type) {
      case 'changeDataSource':
        dispatch({ type, payload: { updateDataSource } });
        break;
      case 'addRow':
        if (!addItem) return;
        const num = flagIndex.current;
        flagIndex.current = Array.isArray(addItem)
          ? num + addItem.length
          : num + 1;
        dispatch({ type, payload: { addItem, flagIndex: num, isEmpty, } });
        break;
      case 'cover':
        if (!addItem) return;
        const coverNum = flagIndex.current;
        flagIndex.current = Array.isArray(addItem)
          ? coverNum + addItem.length
          : coverNum + 1;
        dispatch({ type, payload: { addItem, flagIndex: coverNum, } });
        break;
      case 'append':
        if (!appendItem) return;
        const appendNum = flagIndex.current;
        flagIndex.current = Array.isArray(appendItem)
          ? appendNum + appendItem.length
          : appendNum + 1;
        if (Array.isArray(appendItem) && appendItem.length) {
          dispatch({ type, payload: { appendItem, flagIndex: appendNum, form, } });
        }
        break;
      case 'deleteRow':
        dispatch({ type, payload: { deleteKeys } });
        break;
      case 'moveData':
        dispatch({ type, payload: { startIndex, endIndex } });
        break;
      case 'changeRowData':
        dispatch({ type, payload: { rowData } });
        break;
    }
  }
  return {
    ...EdiTableContext,
    dispatchEdiTable,
  };
}

export default useEdiTableContext;
